"use client";

import type { IHeader5ContactRow } from "@/types/page";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { PhoneFABSVG } from "@/assets/PhoneFABSVG";
import { Phone } from "lucide-react";
import Image from "next/image";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export type Header5ContactItem = {
  label: string;
  icon?: string;
  icon_alt?: string;
  phone_tel: string;
  phone_display?: string;
};

const DEFAULT_BAR_BG = "#291973";
const DEFAULT_PILL_BG = "#ffffff1a";

const FALLBACK_ITEMS: Header5ContactItem[] = [
  // {
  //   label: "Indian Nationals",
  //   phone_tel: "18002102020",
  //   phone_display: "1800 210 2020",
  //   icon_alt: "India",
  // },
  // {
  //   label: "Foreign Nationals",
  //   phone_tel: "+918068792934",
  //   phone_display: "+918068792934",
  //   icon_alt: "International",
  // },
];

function normalizeMetadataInput(
  raw: unknown,
): IHeader5ContactRow[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw as IHeader5ContactRow[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as IHeader5ContactRow[]) : undefined;
    } catch {
      return undefined;
    }
  }
  // MySQL JSON / drivers sometimes deserialize array-like objects { "0": row, "1": row }
  if (typeof raw === "object" && raw !== null) {
    const keys = Object.keys(raw);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
      return Object.values(raw) as IHeader5ContactRow[];
    }
  }
  return undefined;
}

/** Digits / + for tel: — prefers legacy phone_tel, else phone_display */
function rowPhoneForLink(m: IHeader5ContactRow & Record<string, unknown>) {
  const explicit = String(m.phone_tel ?? m.phoneTel ?? "").trim();
  const display = String(m.phone_display ?? m.phoneDisplay ?? "").trim();
  return explicit || display;
}

function rowPhoneDisplayText(m: IHeader5ContactRow & Record<string, unknown>) {
  const display = String(m.phone_display ?? m.phoneDisplay ?? "").trim();
  const fallback = String(m.phone_tel ?? m.phoneTel ?? "").trim();
  return display || fallback;
}

function itemsFromMetadata(
  metadata: IHeader5ContactRow[] | undefined | null,
): Header5ContactItem[] | null {
  if (!metadata?.length) return null;

  const rows = [...metadata]
    .filter((m) => {
      const raw = m as IHeader5ContactRow & Record<string, unknown>;
      return m.status !== false && rowPhoneForLink(raw);
    })
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((m) => {
      const raw = m as IHeader5ContactRow & Record<string, unknown>;
      const link = rowPhoneForLink(raw);
      const disp = rowPhoneDisplayText(raw);
      return {
        label: String(
          m.contact_row_label ?? raw.contactRowLabel ?? "",
        ).trim(),
        icon:
          String(m.contact_row_icon ?? raw.contactRowIcon ?? "").trim() ||
          undefined,
        icon_alt: String(
          m.contact_row_icon_alt ?? raw.contactRowIconAlt ?? "",
        ).trim(),
        phone_tel: link,
        phone_display: disp,
      };
    })
    .filter((m) => m.label && m.phone_tel);

  return rows.length ? rows : null;
}

function parseContactItems(json?: string | null): Header5ContactItem[] {
  if (!json || !String(json).trim()) {
    return FALLBACK_ITEMS;
  }
  try {
    const parsed = JSON.parse(String(json)) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return FALLBACK_ITEMS;
    }
    return parsed
      .filter(
        (row): row is Record<string, unknown> & Header5ContactItem =>
          !!row &&
          typeof row === "object" &&
          typeof (row as Header5ContactItem).label === "string" &&
          (typeof (row as Header5ContactItem).phone_tel === "string" ||
            typeof (row as { phone_display?: string }).phone_display ===
              "string"),
      )
      .map((row) => {
        const tel =
          row.phone_tel ||
          (row as { phone_display?: string }).phone_display ||
          "";
        return {
          ...row,
          phone_tel: tel,
          phone_display: row.phone_display || tel,
        };
      });
  } catch {
    return FALLBACK_ITEMS;
  }
}

function telHref(raw: string) {
  return raw.replace(/[\s()-]/g, "");
}

function pickHeaderStr(
  raw: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function pickStickyToTop(raw: Record<string, unknown>): boolean {
  const v = raw.sticky_to_top ?? raw.stickyToTop;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

function pickBoolFlag(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "on";
  }
  return false;
}

function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

function Header5({ data }: { data: any }) {
  const dispatch = useDispatch();
  const sectionData = data?.data ?? data;

  if (!sectionData) {
    return null;
  }

  const raw = sectionData as Record<string, unknown>;

  const {
    background_color = DEFAULT_BAR_BG,
    pill_chip_background_colour,
    pill_background_color,
    label_text_color = "#FFFFFF",
    phone_text_color = "#FFFFFF",
    contact_items_json,
    metadata,
    show_on_mobile = false,
    show_logo = false,
    logo_image,
    show_apply_button = false,
    apply_button_text = "Apply",
  } = sectionData;

  const phone_icon = pickHeaderStr(raw, "phone_icon", "phoneIcon");
  const phone_icon_alt = pickHeaderStr(raw, "phone_icon_alt", "phoneIconAlt");
  const logoSrc = typeof logo_image === "string" ? logo_image.trim() : "";
  const applyText =
    typeof apply_button_text === "string" && apply_button_text.trim()
      ? apply_button_text.trim()
      : "Apply";

  const stickyToTop = pickStickyToTop(raw);
  const rootRef = useRef<HTMLDivElement>(null);
  const [mobileContactOpen, setMobileContactOpen] = useState(false);

  const openApplyLeadPopup = useCallback(() => {
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch]);

  useLayoutEffect(() => {
    if (!stickyToTop) {
      document.documentElement.style.removeProperty("--header4-sticky-height");
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--header4-sticky-height",
        `${el.offsetHeight}px`,
      );
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--header4-sticky-height");
    };
  }, [stickyToTop]);

  const pillBg =
    pill_chip_background_colour?.trim() ||
    pill_background_color?.trim() ||
    DEFAULT_PILL_BG;

  const barBg = background_color?.trim() || DEFAULT_BAR_BG;

  const metaArr = normalizeMetadataInput(metadata);
  const fromMeta = metaArr?.length ? itemsFromMetadata(metaArr) : null;
  const items =
    fromMeta != null && fromMeta.length > 0
      ? fromMeta
      : parseContactItems(contact_items_json);

  const showLogo = pickBoolFlag(show_logo) && !!logoSrc;
  const showApply = pickBoolFlag(show_apply_button);
  const showOnMobile = pickBoolFlag(show_on_mobile);
  // Even when the full bar is desktop-only, render a compact mobile bar
  // (logo + apply button only) when both toggles are enabled.
  const showMobileCompactBar = !showOnMobile && showLogo && showApply;
  const isVisibleOnMobile = showOnMobile || showMobileCompactBar;
  // When the contact pills are hidden on mobile, surface them through a
  // floating phone button that expands the numbers on tap.
  const showMobileContactFab = !showOnMobile && items.length > 0;

  return (
    <>
    <div
      ref={rootRef}
      className={cn(
        "w-full items-center gap-4 px-4 py-2.5 min-[1024px]:px-9",
        showLogo ? "justify-between" : "justify-end",
        isVisibleOnMobile ? "flex" : "hidden min-[1024px]:flex",
        "shadow-sm",
        stickyToTop && "sticky top-0 z-[100]",
      )}
      style={{ backgroundColor: barBg }}
    >
      {showLogo ? (
        <div className="flex shrink-0 items-center">
          <Image
            src={logoSrc}
            alt="logo"
            width={132}
            height={30}
            className="h-auto w-auto max-h-8 object-contain"
            unoptimized={shouldUnoptimizeImageSrc(logoSrc)}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-4">
      {items.map((item, index) => {
        const display = item.phone_display ?? item.phone_tel;
        const href = `tel:${telHref(item.phone_tel)}`;

        return (
          <div
            key={`${item.label}-${index}`}
            className={cn(
              "items-center gap-3 rounded-md px-4 py-2",
              showMobileCompactBar ? "hidden min-[1024px]:flex" : "flex",
            )}
            style={{ backgroundColor: pillBg }}
          >
            <div className="flex items-center gap-2">
              {item.icon ? (
                <Image
                  alt={item.icon_alt || item.label}
                  className="rounded-sm"
                  width={20}
                  height={15}
                  src={item.icon}
                  unoptimized={shouldUnoptimizeImageSrc(item.icon)}
                />
              ) : null}
              <span
                className="text-xs font-normal"
                style={{ color: label_text_color }}
              >
                {item.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {phone_icon ? (
                <Image
                  src={phone_icon}
                  alt={phone_icon_alt || "Phone"}
                  width={16}
                  height={16}
                  className="size-4 shrink-0 object-contain"
                  unoptimized={shouldUnoptimizeImageSrc(phone_icon)}
                />
              ) : (
                <Phone
                  aria-hidden
                  className="size-4 shrink-0"
                  style={{
                    color: label_text_color,
                    fill: label_text_color,
                  }}
                  strokeWidth={0.5}
                />
              )}
              <a
                href={href}
                className="text-xs font-semibold"
                style={{ color: phone_text_color }}
              >
                {display}
              </a>
            </div>
          </div>
        );
      })}
        {showApply ? (
          <button
            type="button"
            onClick={openApplyLeadPopup}
            className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary px-6 py-1.5 text-xs font-medium text-white transition-colors focus-visible:outline-none focus-visible:ring-1 min-[768px]:text-sm"
          >
            {applyText}
          </button>
        ) : null}
      </div>
    </div>

      {/* `data-mobile-float-dock` lets FloatingIcons stack the promotional icon
          directly above this column instead of over it. */}
      {showMobileContactFab ? (
        <div
          data-mobile-float-dock
          className="fixed bottom-5 left-4 z-[120] flex flex-col items-start gap-3 min-[1024px]:hidden"
        >
          {mobileContactOpen ? (
            <div className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
              {items.map((item, index) => {
                const display = item.phone_display ?? item.phone_tel;
                const href = `tel:${telHref(item.phone_tel)}`;
                return (
                  <a
                    key={`${item.label}-mobile-${index}`}
                    href={href}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3",
                      index > 0 && "border-t border-gray-200",
                    )}
                  >
                    {item.icon ? (
                      <Image
                        alt={item.icon_alt || item.label}
                        className="rounded-sm"
                        width={28}
                        height={20}
                        src={item.icon}
                        unoptimized={shouldUnoptimizeImageSrc(item.icon)}
                      />
                    ) : null}
                    <div className="flex flex-col">
                      <span className="text-xs leading-tight text-gray-500">
                        {item.label}
                      </span>
                      <span className="text-base font-bold text-gray-900">
                        {display}
                      </span>
                    </div>
                    <Phone
                      aria-hidden
                      className="ml-2 size-4 shrink-0 text-blue-600"
                    />
                  </a>
                );
              })}
            </div>
          ) : null}
          <button
            type="button"
            aria-label="Contact us"
            aria-expanded={mobileContactOpen}
            onClick={() => setMobileContactOpen((open) => !open)}
            className="p-0 bg-transparent border-none cursor-pointer hover:scale-110 transition-transform"
          >
            <PhoneFABSVG isOpen={mobileContactOpen} />
          </button>
        </div>
      ) : null}
    </>
  );
}

export default Header5;